import { Facebook, Instagram, Linkedin, Heart, Plane } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'

const navigation = {
  main: [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Holiday Packages', href: '/packages' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact us', href: '/contact' },
  ],
}

export default function Footer() {
  const { franchiseData, loading } = useFranchiseData()

  if (loading || !franchiseData) {
    return (
      <footer className="bg-white border-t border-gray-200">
        <div className="mx-auto max-w-7xl overflow-hidden px-6 py-12 sm:py-16 lg:px-8">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded w-48 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-32"></div>
          </div>
        </div>
      </footer>
    )
  }

  const socialLinks = [
    {
      name: 'Facebook',
      href: franchiseData.facebook || '#',
      icon: Facebook,
    },
    {
      name: 'Instagram',
      href: franchiseData.instagram || '#',
      icon: Instagram,
    },
    {
      name: 'LinkedIn',
      href: franchiseData.linkedin || '#',
      icon: Linkedin,
    },
  ].filter((link) => link.href !== '#') // Only show links that exist

  return (
    <footer className="bg-[#151c26] text-gray-200">
      <div className="mx-auto max-w-7xl overflow-hidden px-6 py-12 sm:py-16 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4 lg:items-center">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <p className="mt-4 text-base text-gray-300">
              Your trusted travel partner for unforgettable experiences around the world. We create
              memories that last a lifetime.
            </p>
            <div className="mt-4 flex items-center">
              <span className="text-[#1ebbd7] font-bold mr-2">sa</span>
              <span className="text-white font-semibold">Proudly South African</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
              Quick Links
            </h3>
            <ul className="space-y-4">
              {navigation.main.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    className="text-gray-300 hover:text-[#1ebbd7] transition-colors text-base"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-semibold text-white mb-4 uppercase tracking-wider">
              Contact Info
            </h3>
            <div className="space-y-2">
              {franchiseData.telephone && (
                <div className="flex items-center space-x-2 text-base text-gray-300">
                  <span className="text-[#58b8cd]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="inline h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a2 2 0 011.7.95l1.54 2.6a2 2 0 01-.17 2.18l-1.1 1.65a11.042 11.042 0 005.516 5.516l1.65-1.1a2 2 0 012.18-.17l2.6 1.54a2 2 0 01.95 1.7V19a2 2 0 01-2 2h-1C7.82 21 3 16.18 3 10V9a2 2 0 012-2z"
                      />
                    </svg>
                  </span>
                  <a
                    href={`tel:${franchiseData.telephone.replace(/[^\d+]/g, '')}`}
                    className="hover:text-[#1ebbd7] transition-colors"
                  >
                    {franchiseData.telephone}
                  </a>
                </div>
              )}
              {franchiseData.email && (
                <div className="flex items-center space-x-2 text-base text-gray-300">
                  <span className="text-[#58b8cd]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="inline h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 12H8m8 0a4 4 0 11-8 0 4 4 0 018 0zm0 0v1a4 4 0 01-8 0v-1"
                      />
                    </svg>
                  </span>
                  <a
                    href={`mailto:${franchiseData.email}`}
                    className="hover:text-[#1ebbd7] transition-colors"
                  >
                    {franchiseData.email}
                  </a>
                </div>
              )}
              {franchiseData.address && (
                <div className="flex items-start space-x-2 text-base text-gray-300">
                  <span className="text-[#58b8cd]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="inline h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 21l-4.243-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </span>
                  <span>
                    {franchiseData.address
                      .split(/\r?\n/)
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line, index, lines) => (
                        <span key={index}>
                          {line}
                          {index < lines.length - 1 && <br />}
                        </span>
                      ))}
                  </span>
                </div>
              )}
              <div className="text-sm text-gray-400 mt-2">
                Working hours: Monday to Friday 08.30 to 17.00 SAST
              </div>
              <div className="text-sm text-gray-400 mt-2">
                The office is open Tuesday to Thursday from 09.00 to 14.00 SAST
              </div>
            </div>
          </div>

          {/* Logo Section (moved from brand) */}
          <div className="lg:col-span-1 flex items-center justify-center">
            <img
              src="/CT-VERTICAL-LOGO-BLUE.png"
              alt="Chelsea Travel Vertical Logo"
              className="h-25 w-auto"
            />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 border-t border-[#232b36] pt-8">
          <div className="flex flex-col items-center space-y-4 lg:flex-row lg:justify-between lg:space-y-0">
            <p className="text-xs text-gray-400">&copy; 1998. All rights reserved.</p>
            <div className="flex flex-wrap gap-6 text-xs text-gray-400">
              <a href="#" className="hover:text-[#1ebbd7] transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-[#1ebbd7] transition-colors">
                Terms of Service
              </a>
              <a href="#" className="hover:text-[#1ebbd7] transition-colors">
                Travel Insurance
              </a>
            </div>
            <p className="text-xs text-gray-400 flex items-center">
              Made with <Heart className="h-3 w-3 text-red-500 mx-1" /> for travelers
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
